<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <file-drop-zone
    :disabled="disableDrop || entry.isFile()"
    @files-added="$emit('filesAdded', $event, !entry.isFile() ? entry.name : undefined)"
    :is-reader="isWorkspaceReader"
    class="drop-zone-item"
    @drop-as-reader="$emit('dropAsReader')"
  >
    <ion-item
      button
      :lines="isLargeDisplay ? 'full' : 'none'"
      :detail="false"
      class="list-item file-list-item"
      :class="{
        selected: selected,
        'file-list-item--hovered': !selected && (menuOpened || isHovered),
        'file-list-item-mobile': isSmallDisplay,
      }"
      @dblclick="$emit('openItem', $event, entry)"
      @click="$emit('update:selected', !selected)"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
      @contextmenu="onOptionsClick"
    >
      <div class="list-item-container file-list-item-container">
        <div
          class="list-item-column file-selected"
          v-if="isLargeDisplay || selected || showCheckbox"
        >
          <ms-checkbox
            @change="$emit('update:selected', !selected)"
            :checked="selected"
            v-show="selected || isHovered || showCheckbox"
            @click.stop
            @dblclick.stop
          />
        </div>

        <!-- file name -->
        <div
          class="list-item-column file-name"
          :class="{ 'file-mobile-content': isSmallDisplay }"
        >
          <ms-image
            :image="entry.isFile() ? getFileIcon(entry.name) : Folder"
            class="file-icon"
          />

          <!-- large display view -->
          <ion-text
            v-if="isLargeDisplay"
            class="list-item-label label-name cell"
            :class="{ selection: showCheckbox }"
            :title="entry.name"
            @click="showCheckbox ? null : !($event.metaKey || $event.ctrlKey) && $emit('openItem', $event, entry)"
            @dblclick.stop
          >
            {{ entry.name }}
          </ion-text>

          <ion-icon
            class="cloud-overlay"
            :class="syncStatus.class"
            :icon="syncStatus.icon"
          />

          <!-- small display view -->
          <div
            v-if="isSmallDisplay"
            class="file-mobile-text"
          >
            <ion-text
              class="list-item-label label-name cell"
              :class="{ selection: showCheckbox }"
              :title="entry.name"
              @click="showCheckbox ? null : !($event.metaKey || $event.ctrlKey) && $emit('openItem', $event, entry)"
              @dblclick.stop
            >
              {{ entry.name }}
            </ion-text>
            <ion-text class="list-item-label label-data body-sm">
              <span class="data-date">{{ $msTranslate(formatTimeSince(entry.updated, '--', 'short')) }}</span>
              <span v-if="entry.isFile()"> &bull; </span>
              <span
                class="data-size"
                v-if="entry.isFile()"
              >
                {{ $msTranslate(formatFileSize((entry as FileModel).size)) }}
              </span>
            </ion-text>
          </div>

          <span
            v-if="entry.syncStatus === EntrySyncStatus.Uploading && entry.syncProgress"
            class="upload-progress button-small"
          >
            {{ entry.syncProgress }}%
          </span>
        </div>

        <!-- updated by -->
        <div
          class="list-item-column file-updated-by"
          v-if="entry.lastUpdater && isLargeDisplay && ownProfile !== UserProfile.Outsider"
        >
          <user-avatar-name
            :user-avatar="entry.lastUpdater.humanHandle.label"
            :user-name="entry.lastUpdater.humanHandle.label"
          />
        </div>

        <!-- last update -->
        <div class="list-item-column file-last-update">
          <ion-text class="list-item-label label-last-update cell">
            {{ $msTranslate(formatTimeSince(entry.updated, '--', 'short')) }}
          </ion-text>
        </div>

        <!-- creation date -->
        <div class="list-item-column file-creation-date">
          <ion-text class="list-item-label label-creation-date cell">
            {{ $msTranslate(formatTimeSince(entry.created, '--', 'short')) }}
          </ion-text>
        </div>

        <!-- file size -->
        <div class="list-item-column file-size">
          <ion-text
            v-if="entry.isFile()"
            class="list-item-label label-size cell"
          >
            {{ $msTranslate(formatFileSize((entry as FileModel).size)) }}
          </ion-text>
        </div>

        <!-- options -->
        <div class="list-item-end file-options ion-item-child-clickable">
          <ion-button
            fill="clear"
            v-show="isHovered || menuOpened || isSmallDisplay"
            class="options-button"
            @click.stop="onOptionsClick($event)"
            @dblclick.stop
          >
            <ion-icon
              :icon="ellipsisHorizontal"
              slot="icon-only"
              class="options-button__icon"
            />
          </ion-button>
        </div>
      </div>
    </ion-item>
  </file-drop-zone>
</template>

<script setup lang="ts">
import { formatFileSize, getFileIcon } from '@/common/file';
import FileDropZone from '@/components/files/explorer/FileDropZone.vue';
import { EntryModel, EntrySyncStatus, FileModel } from '@/components/files/types';
import UserAvatarName from '@/components/users/UserAvatarName.vue';
import { EntryName, UserProfile } from '@/parsec';
import { IonButton, IonIcon, IonItem, IonText } from '@ionic/vue';
import { cloudDone, cloudOffline, cloudUpload, ellipsisHorizontal } from 'ionicons/icons';
import { Folder, formatTimeSince, MsCheckbox, MsImage, useWindowSize } from 'megashark-lib';
import { computed, ref } from 'vue';

const isHovered = ref(false);
const menuOpened = ref(false);
const { isSmallDisplay, isLargeDisplay } = useWindowSize();

const props = defineProps<{
  ownProfile: UserProfile;
  entry: EntryModel;
  showCheckbox: boolean;
  isWorkspaceReader?: boolean;
  disableDrop?: boolean;
  selected: boolean;
}>();

const emits = defineEmits<{
  (e: 'click', event: Event, entry: EntryModel): void;
  (e: 'openItem', event: Event, entry: EntryModel): void;
  (e: 'menuClick', event: Event, entry: EntryModel, onFinished: () => void): void;
  (e: 'filesAdded', gen: AsyncGenerator<File[]>, destinationFolder?: EntryName): void;
  (e: 'dropAsReader'): void;
  (e: 'update:selected', value: boolean): void;
}>();

defineExpose({
  isHovered,
  props,
});

const syncStatus = computed(() => {
  switch (props.entry.syncStatus) {
    case EntrySyncStatus.Synced:
      return { class: 'cloud-overlay-ok', icon: cloudDone };
    case EntrySyncStatus.Uploading:
      return { class: 'cloud-overlay-ko', icon: cloudUpload };
    default:
      return { class: 'cloud-overlay-ko', icon: cloudOffline };
  }
});

async function onOptionsClick(event: PointerEvent): Promise<void> {
  event.preventDefault();
  event.stopPropagation();

  menuOpened.value = true;
  emits('menuClick', event, props.entry, () => (menuOpened.value = false));
}
</script>

<style lang="scss" scoped>
.drop-zone-item {
  height: fit-content;
}
</style>
